import { InformationCircleIcon as InformationCircleIconOutline } from '@heroicons/react/outline';
import {
  CogIcon,
  ExclamationIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/solid';
import { makeStyles } from '@material-ui/core';
import Fade from '@material-ui/core/Fade';
import Tooltip from '@material-ui/core/Tooltip';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useRouter } from 'next/router';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import Loader from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import Select, { components } from 'react-select';

import ajaxPromise from '../utils/ajaxPromise';
import { MonthNames } from '../utils/constants';

const useStyles = makeStyles({
  customTooltip: {
    backgroundColor: '#000000',
    fontSize: '0.8rem',
    lineHeight: '1.2rem',
  },
  customArrow: {
    color: '#000000',
  },
  smallFont: {
    fontSize: '0.8rem',
    lineHeight: '1.1rem',
  },
});

export default function CreditConsumption() {
  dayjs.extend(utc);
  const filterDateOptions = [
    { value: 'All Time', label: 'All Time' },
    { value: 'Today', label: 'Today' },
    { value: 'Yesterday', label: 'Yesterday' },
    { value: 'Last 7 days', label: 'Last 7 days' },
    { value: 'Current Month', label: 'Current Month' },
    { value: 'Current Year', label: 'Current Year' },
    { value: 'Custom', label: 'Custom' },
  ];
  const filterIntervalOptions = [
    { value: '1y', label: 'Years' },
    { value: '1q', label: 'Quarters' },
    { value: '1M', label: 'Months' },
    { value: '1w', label: 'Weeks' },
    { value: '1d', label: 'Days' },
    { value: '1h', label: 'Hours' },
    // { value: '1m', label: 'Minutes' },
  ];
  const sourceTypesOptions = [
    { value: 'Platform', label: 'Platform' },
    { value: 'API', label: 'API' },
    { value: 'Chrome Extension', label: 'Chrome Extension' },
  ];

  const allFilterSourceOptions = [
    { value: '*', label: 'All Sources' },
    {
      value: 'Enriched a contact (Platform)',
      label: 'Enriched a contact (Platform)',
      backgroundColor: '#12383b',
      kind: 'Platform',
    },
    {
      value: 'Exported a list from contact exact match (Platform)',
      label: 'Exported a list from contact exact match (Platform)',
      backgroundColor: '#4770B3',
      kind: 'Platform',
    },
    {
      value: 'Exported a list from company exact match (Platform)',
      label: 'Exported a list from company exact match (Platform)',
      backgroundColor: '#6e3a62',
      kind: 'Platform',
    },
    {
      value: 'Exported a dynamic contact list (Platform)',
      label: 'Exported a dynamic contact list (Platform)',
      backgroundColor: '#569D79',
      kind: 'Platform',
    },
    {
      value: 'Revealed contacts in Prospector (Platform)',
      label: 'Revealed contacts in Prospector (Platform)',
      backgroundColor: '#CAD93F',
      kind: 'Platform',
    },
    {
      value: 'Refreshed saved contacts in a list (Platform)',
      label: 'Refreshed saved contacts in a list (Platform)',
      backgroundColor: '#757210',
      kind: 'Platform',
    },
    {
      value: 'Enriched a contact (API)',
      label: 'Enriched a contact (API)',
      backgroundColor: '#781154',
      kind: 'API',
    },
    {
      value: 'Enriched a company (API)',
      label: 'Enriched a company (API)',
      backgroundColor: '#84db9a',
      kind: 'API',
    },
    {
      value: 'Bulk enriched contacts (API)',
      label: 'Bulk enriched contacts (API)',
      backgroundColor: '#dbae84',
      kind: 'API',
    },
    {
      value: 'Prospector API for contacts (API)',
      label: 'Prospector API for contacts (API)',
      backgroundColor: '#db8484',
      kind: 'API',
    },
    {
      value: 'Verified an email (API)',
      label: 'Verified an email (API)',
      backgroundColor: '#b895b1',
      kind: 'API',
    },
    {
      value: 'Enriched a contact (Chrome Extension)',
      label: 'Enriched a contact (Chrome Extension)',
      backgroundColor: '#73c9c4',
      kind: 'Chrome Extension',
    },
    {
      value: 'Enriched a company (Chrome Extension)',
      label: 'Enriched a company (Chrome Extension)',
      backgroundColor: '#737dc9',
      kind: 'Chrome Extension',
    },
  ];
  const { user } = useSelector((state) => state.user);

  const intervalDateFormat = {
    '1y': 'YYYY',
    '1q': 'MM/YYYY',
    '1M': 'MMMM YYYY',
    '1w': 'MM/DD/YYYY',
    '1d': 'MM/DD/YYYY',
    '1h': 'MM/DD/YYYY hh A',
  };
  const [usageOverview, setUsageOverview] = React.useState(true);
  const [loadingOverview, setloadingOverview] = React.useState(false);
  const [filterDate, setFilterDate] = React.useState(filterDateOptions[0]);
  const [filterUserOptions, setFilterUserOptions] = React.useState([
    { value: 'All Users', label: 'All Users' },
  ]);
  const [filterUser, setFilterUser] = React.useState();
  const [loadingUserOptions, setLoadingUserOptions] = React.useState(false);
  const [filterInterval, setFilterInterval] = React.useState(filterIntervalOptions[4]);
  const [loadingGraph, setLoadingGraph] = React.useState(false);
  const [loadingGraphError, setLoadingGraphError] = React.useState(false);
  const [rawGraphData, setRawGraphData] = React.useState(null);
  const [graphTotalCredits, setGraphTotalCredits] = React.useState(0);
  const [startTimestamp, setStartTimestamp] = React.useState(1639767600);
  const [endTimestamp, setEndTimestamp] = React.useState('latest');
  const [intervalLengthError, setIntervalLengthError] = React.useState(false);
  const [noGraphData, setNoGraphData] = React.useState(false);
  const [filterSource, setFilterSource] = React.useState(allFilterSourceOptions);
  const [sourceTypes, setSourceTypes] = React.useState(sourceTypesOptions);
  const [filterSourceOptions, setFilterSourceOptions] = React.useState([...allFilterSourceOptions]);

  const classes = useStyles();
  const router = useRouter();

  const teamAdminView = user?.permission_profile?.VIEW_CREDIT_CONSUMPTION_ORGANIZATION;

  React.useEffect(() => {
    setloadingOverview(true);
    setLoadingUserOptions(true);
    ajaxPromise('/api/usage_stats', 'GET')
      .then((response) => {
        setUsageOverview(response.data);
      })
      .catch(() => {})
      .then(() => {
        setloadingOverview(false);
      });
  }, []);

  React.useEffect(() => {
    if (teamAdminView) {
      setFilterUser({ value: 'All Users', label: 'All Users' });
      ajaxPromise('/api/organizations/team_members', 'GET')
        .then((response) => {
          setFilterUserOptions(response.data);
          setLoadingUserOptions(false);
        })
        .catch(() => {
          setLoadingUserOptions(false);
        });
    }
  }, [teamAdminView]);

  React.useEffect(() => {
    setIntervalLengthError(false);
    const endTime = endTimestamp === 'latest' ? dayjs.utc().unix() : endTimestamp;
    if (filterInterval.value === '1h' && endTime - startTimestamp > 714800) {
      setIntervalLengthError(true);
      return;
    }
    setLoadingGraph(true);
    setLoadingGraphError(false);
    setNoGraphData(false);
    ajaxPromise(
      `/v1/credit_usage/detail?end_timestamp=${endTimestamp}&start_timestamp=${startTimestamp}&interval_type=${
        filterInterval.value
      }&usage_type=${filterSource.value}&filter_user=${filterUser ? filterUser?.value : ''}`,
      'GET',
    )
      .then((response) => {
        setRawGraphData(response.data.graph_data);
        setGraphTotalCredits(response.data.total_credits_consumed);

        setLoadingGraph(false);
        if (Object.keys(response.data).length === 0) {
          setNoGraphData(true);
        }
      })
      .catch(() => {
        setLoadingGraph(false);
        setLoadingGraphError(true);
      });
  }, [filterUser, filterDate, filterInterval, startTimestamp, endTimestamp]);

  const handleFilterDateChange = (event) => {
    setFilterDate(event);
    const currentTime = new Date();
    const currentDate = currentTime.getUTCDate();
    const currentMonth = currentTime.getUTCMonth();
    const currentYear = currentTime.getUTCFullYear();
    setEndTimestamp('latest');
    if (event.value === 'All Time') {
      setStartTimestamp(0);
    } else if (event.value === 'Today') {
      setStartTimestamp(Date.UTC(currentYear, currentMonth, currentDate) / 1000);
    } else if (event.value === 'Yesterday') {
      setStartTimestamp(Date.UTC(currentYear, currentMonth, currentDate - 1) / 1000);
    } else if (event.value === 'Last 7 days') {
      setStartTimestamp(Date.UTC(currentYear, currentMonth, currentDate - 7) / 1000);
    } else if (event.value === 'Current Month') {
      setStartTimestamp(Date.UTC(currentYear, currentMonth) / 1000);
    } else if (event.value === 'Current Year') {
      setStartTimestamp(Date.UTC(currentYear) / 1000);
    }
  };

  const handleFilterSourceChange = (value, event) => {
    if (event.action === 'select-option' && event.option.value === '*') {
      setFilterSource(filterSourceOptions);
    } else if (event.action === 'deselect-option' && event.option.value === '*') {
      setFilterSource([]);
    } else if (event.action === 'deselect-option') {
      setFilterSource(value.filter((o) => o.value !== '*'));
    } else if (value.length === filterSourceOptions.length - 1) {
      setFilterSource(filterSourceOptions);
    } else {
      setFilterSource(value);
    }
  };

  const handleFilterIntervalChange = (event) => {
    setFilterInterval(event);
  };

  const handleFilterUserChange = (event) => {
    setFilterUser(event);
  };

  const handleSourceTypesChange = (event) => {
    setSourceTypes(event);
    const sourceTypeArray = event.map((e) => e.value);
    let filteredList = allFilterSourceOptions.filter((e) => sourceTypeArray.includes(e?.kind));
    if (filteredList.length > 0) {
      filteredList = [{ value: '*', label: 'All Sources' }, ...filteredList];
    }
    setFilterSourceOptions(filteredList);
    setFilterSource(filteredList);
  };

  const getTooltipHover = (event) => {
    const sum = event.reduce((a, b) => a + b.raw, 0);
    return `Date: ${event[0]?.label}\nTotal Credits Consumed: ${sum}`;
  };

  const getSourceTypes = () => {
    const graphSourceTypes = {};
    Object.keys(filterSource).forEach((e) => {
      if (filterSource[e].value === '*') {
        return;
      }
      graphSourceTypes[filterSource[e].value] = {
        label: filterSource[e].value,
        data: [],
        backgroundColor: filterSource[e].backgroundColor,
        maxBarThickness: 100,
      };
    });
    return graphSourceTypes;
  };

  const formatGraphData = () => {
    const graphData = rawGraphData;
    if (!graphData) return {};

    const graphSourceTypes = getSourceTypes();
    const labels = [];
    Object.keys(graphData).forEach((element) => {
      let tt = dayjs.unix(element);
      tt = tt.utc();
      labels.push(tt.format(intervalDateFormat[filterInterval.value]));
      Object.keys(graphSourceTypes).forEach((e) => {
        if (graphData?.[element]?.[e]) {
          graphSourceTypes[e].data.push(graphData?.[element]?.[e]?.credits_consumed);
        } else {
          graphSourceTypes[e].data.push(0);
        }
      });
    });
    const datasets = [];
    Object.keys(graphSourceTypes).forEach((e) => {
      datasets.push(graphSourceTypes[e]);
    });
    const chartData = {
      labels,
      datasets,
    };
    return chartData;
  };

  const MultiValue = ({ getValue, index }) =>
    !index &&
    `${getValue().length === filterSourceOptions.length ? 'All' : getValue().length} ${
      getValue().length === 1 ? 'Source' : 'Sources'
    } selected`;

  const Option = (props) => (
    <div>
      <components.Option {...props}>
        <div className='flex items-center justify-between space-x-2'>
          <label>{props.label}</label>
          <input
            type='checkbox'
            checked={props.isSelected}
            onChange={() => null}
            style={{ height: 14, width: 14, float: 'right' }}
          />
        </div>
      </components.Option>
    </div>
  );

  return (
    <div className='px-4 mt-2'>
      <div className=''>
        <h3 className='text-lg leading-6 font-medium text-gray-900'>Personal Overview</h3>

        <dl className='mt-5 grid grid-cols-2 rounded-lg bg-white overflow-hidden shadow divide-y divide-gray-200 md:grid-cols-4 md:divide-y-0 md:divide-x'>
          <div className='px-4 py-2 sm:p-4'>
            <dt className='text-sm font-normal text-gray-900'>Credits Remaining</dt>
            <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
              {!loadingOverview ? (
                <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                  {usageOverview.credits_remaining}
                </div>
              ) : (
                <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
              )}
            </dd>
          </div>
          <div className='px-4 py-2 sm:p-4'>
            <dt className='text-sm font-normal text-gray-900'>Total Credits Consumed</dt>
            <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
              {!loadingOverview ? (
                <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                  {usageOverview.credits_consumed}
                </div>
              ) : (
                <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
              )}
            </dd>
          </div>

          <div className='px-4 py-2 sm:p-4'>
            <dt className='text-sm font-normal text-gray-900'>{`Credits Consumed - ${
              MonthNames[dayjs().month()]
            }`}</dt>
            <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
              {!loadingOverview ? (
                <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                  {usageOverview.credits_consumed_this_month}
                </div>
              ) : (
                <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
              )}
            </dd>
          </div>
          <div className='px-4 py-2 sm:p-4'>
            <dt className='text-sm font-normal text-gray-900'>{`Credits Consumed - ${dayjs().year()}`}</dt>
            <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
              {!loadingOverview ? (
                <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                  {usageOverview.credits_consumed_this_year}
                </div>
              ) : (
                <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
              )}
            </dd>
          </div>
        </dl>
      </div>
      {user?.permission_profile?.ORGANIZATION_VIEW_ACCESS && (
        <div className='mt-4'>
          <h3 className='text-lg leading-6 font-medium text-gray-900'>Team Overview</h3>

          <dl className='mt-5 grid grid-cols-2 rounded-lg bg-white overflow-hidden shadow divide-y divide-gray-200 md:grid-cols-4 md:divide-y-0 md:divide-x'>
            <div className='px-4 py-2 sm:p-4'>
              <dt className='text-sm font-normal text-gray-900'>Team Credits Remaining</dt>
              <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
                {!loadingOverview ? (
                  <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                    {usageOverview.team_total_remaining_credits}
                  </div>
                ) : (
                  <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
                )}
              </dd>
            </div>
            <div className='px-4 py-2 sm:p-4'>
              <dt className='text-sm font-normal text-gray-900'>Team Total Credits Consumed</dt>
              <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
                {!loadingOverview ? (
                  <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                    {usageOverview.team_total_consumed_credits}
                  </div>
                ) : (
                  <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
                )}
              </dd>
            </div>

            <div className='px-4 py-2 sm:p-4'>
              <dt className='text-sm font-normal text-gray-900'>{`Team Credits Consumed - ${
                MonthNames[dayjs().month()]
              }`}</dt>
              <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
                {!loadingOverview ? (
                  <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                    {usageOverview.team_credits_consumed_this_month}
                  </div>
                ) : (
                  <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
                )}
              </dd>
            </div>
            <div className='px-4 py-2 sm:p-4'>
              <dt className='text-sm font-normal text-gray-900'>{`Team Credits Consumed - ${dayjs().year()}`}</dt>
              <dd className='mt-1 flex justify-between items-baseline md:block lg:flex'>
                {!loadingOverview ? (
                  <div className='flex items-baseline text-xl font-semibold text-exact-blue'>
                    {usageOverview.team_credits_consumed_this_year}
                  </div>
                ) : (
                  <Loader type='ThreeDots' color='#5046E4' height={24} width={24} />
                )}
              </dd>
            </div>
          </dl>
        </div>
      )}
      <div className='mt-4'>
        <h3 className='text-lg leading-6 font-medium text-gray-900'>Detailed View</h3>
        <div className='flex justify-between space-x-8 align-middle'>
          <div className='bg-blue-50 border-l-4 border-exact-blue p-4 mt-3 grow'>
            <div className='flex'>
              <div className='shrink-0'>
                <InformationCircleIcon className='h-5 w-5 text-exact-blue' aria-hidden='true' />
              </div>
              <div className='ml-3 flex-1 md:flex md:justify-between'>
                <p className='text-sm text-exact-blue'>
                  Detailed view only displays credits consumed after December 20th 2021.
                </p>
              </div>
            </div>
          </div>
          {user?.permission_profile?.ORGANIZATION_VIEW_ACCESS && (
            <button
              type='button'
              onClick={() => {
                router.push('organization');
              }}
              className='my-3 inline-flex items-center p-2 bg-exact-blue hover:bg-blue-900 shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
            >
              <CogIcon className='w-5 h-5 mr-1' />
              Manage Team
            </button>
          )}
        </div>

        <div
          className={`grid grid-cols-2 ${
            teamAdminView ? 'md:grid-cols-3' : 'md:grid-cols-4'
          } gap-4 items-center mt-4`}
        >
          <div>
            <label className='block text-sm font-medium text-gray-700'>
              <Tooltip
                classes={{
                  tooltip: classes.customTooltip,
                  arrow: classes.customArrow,
                }}
                title='All time inputs and displays are in GMT time.'
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
                arrow
                interactive
                placement='right'
              >
                <div className='flex items-center justify-between'>
                  Filter Time (GMT)
                  <InformationCircleIconOutline className='h-5 w-5 ml-1 mt-0.5 text-gray-400' />
                </div>
              </Tooltip>
            </label>

            <Select
              options={filterDateOptions}
              className='mt-1 block w-full text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
              onChange={handleFilterDateChange}
              isDisabled={loadingGraph}
              isLoading={loadingGraph}
              isSearchable={false}
              value={filterDate}
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700'>Filter Interval</label>
            <Select
              options={filterIntervalOptions}
              className='mt-1 block w-full text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
              onChange={handleFilterIntervalChange}
              isDisabled={loadingGraph}
              isLoading={loadingGraph}
              isSearchable={false}
              value={filterInterval}
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700'>Filter Source</label>
            <Select
              className='mt-1 block w-full text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
              options={filterSourceOptions}
              onChange={handleFilterSourceChange}
              isDisabled={loadingGraph}
              isLoading={loadingGraph}
              value={filterSource}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ MultiValue, Option }}
              isSearchable={false}
              noOptionsMessage={() => 'Enable at least one Source in the Source Types filter'}
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700'>Source Types</label>
            <Select
              options={sourceTypesOptions}
              isMulti
              className='mt-1 block w-full text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
              onChange={handleSourceTypesChange}
              isDisabled={loadingGraph}
              isLoading={loadingGraph}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              isSearchable={false}
              value={sourceTypes}
              components={{ Option }}
            />
          </div>

          {teamAdminView && (
            <div>
              <label className='block text-sm font-medium text-gray-700'>Filter Users</label>
              <Select
                className='mt-1 block w-full text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
                options={filterUserOptions}
                onChange={handleFilterUserChange}
                isDisabled={loadingGraph || loadingUserOptions}
                isLoading={loadingGraph || loadingUserOptions}
                value={filterUser}
                closeMenuOnSelect={false}
                isSearchable
                noOptionsMessage={() => 'No users found'}
              />
            </div>
          )}
        </div>
      </div>

      {filterDate.value === 'Custom' && (
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4 items-center mt-4'>
          <div className=' flex-col space-y-2'>
            <label className='block text-sm font-medium text-gray-700'>Start Date</label>
            <input
              type='date'
              className='mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
              onChange={(e) => {
                setStartTimestamp(dayjs(e.target.valueAsDate).unix());
              }}
            />
          </div>

          <div className='flex-col space-y-2'>
            <label className='block text-sm font-medium text-gray-700'>End Date</label>
            <input
              type='date'
              className='mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
              onChange={(e) => {
                setEndTimestamp(dayjs(e.target.valueAsDate).unix());
              }}
            />
          </div>
        </div>
      )}

      {loadingGraph && (
        <div className='flex justify-center m-32'>
          <Loader type='Bars' color='#3F51B5' height={48} width={48} />
        </div>
      )}
      {rawGraphData && !loadingGraph && !loadingGraphError && !noGraphData && !intervalLengthError && (
        <div className='flex justify-center px-6'>
          <Bar
            data={formatGraphData()}
            className='mt-12 max-w-7xl max-h-96'
            options={{
              indexAxis: 'x',
              scales: {
                x: {
                  stacked: true,
                  title: {
                    color: '#3F51B5',
                    display: true,
                    text: 'Timeline',
                  },
                },
                y: {
                  stacked: true,
                  label: 'Credits',
                  ticks: {
                    min: 0,
                    stepSize: 1,
                  },
                  title: {
                    color: '#3F51B5',
                    display: true,
                    text: 'Credits Consumed',
                  },
                },
              },
              plugins: {
                tooltip: {
                  callbacks: {
                    title: (tooltipItems) => getTooltipHover(tooltipItems),
                  },
                },
                title: {
                  display: true,
                  text: `Total consumed credits: ${graphTotalCredits}`,
                  align: 'center',
                  font: {
                    size: 18,
                  },
                },
              },
            }}
          />
        </div>
      )}
      {loadingGraphError && (
        <div className='rounded-md bg-red-50 p-4 mt-12'>
          <div className='flex'>
            <div className='shrink-0'>
              <XCircleIcon className='h-5 w-5 text-red-400' aria-hidden='true' />
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-800'>
                There was an error fetching usage details.{' '}
              </h3>
              <div className='mt-2 text-sm text-red-700'>
                <ul role='list' className='list-disc pl-5 space-y-1'>
                  <li>
                    If the issue persists, please{' '}
                    <a href='mailto:support@exactbuyer.com' className='text-blue-600'>
                      contact us
                    </a>{' '}
                    .
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {noGraphData && (
        <div className='bg-blue-50 border-l-4 border-exact-blue p-4 mt-3'>
          <div className='flex'>
            <div className='shrink-0'>
              <XCircleIcon className='h-5 w-5 text-exact-blue' aria-hidden='true' />
            </div>
            <div className='ml-3'>
              <h3 className='text-sm text-exact-blue'>
                No data was found for the selected filters.{' '}
              </h3>
            </div>
          </div>
        </div>
      )}

      {intervalLengthError && (
        <div className='bg-yellow-50 border-l-4 border-yellow-900 p-4 mt-3'>
          <div className='flex'>
            <div className='shrink-0'>
              <ExclamationIcon className='h-5 w-5 text-yellow-900' aria-hidden='true' />
            </div>
            <div className='ml-3'>
              <p className='text-sm text-yellow-900'>
                The hour interval cannot be used for timespans greater than 7 days.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
